import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cake, Star, X } from "lucide-react";
import api from "@/lib/api";

const fmt = (n) => String((n || 0).toFixed(2)).replace(".", ",") + " €";

export default function CrmManager() {
  const [clients, setClients] = useState([]);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = () => api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const openDetail = async (id) => {
    const { data } = await api.get(`/admin/clients/${id}`);
    setDetail(data);
  };

  const saveDetail = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/admin/clients/${detail.id}`, {
        birthday: detail.birthday || "",
        notes: detail.notes || "",
        preferences: detail.preferences || "",
      });
      toast.success("Fiche client enregistrée.");
      setDetail(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = clients.filter(
    (c) =>
      !search ||
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="crm-manager">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ivory">Clients — {clients.length}</h2>
          <p className="mt-1 text-xs text-ivory/40">
            Fiches créées automatiquement après chaque réservation ou commande payée. Points de
            fidélité : 1 point par tranche de 10 € dépensés.
          </p>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un client…"
          className="input-dark !h-10 w-64 !text-xs"
          data-testid="crm-search-input"
        />
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="crm-detail-modal">
          <form onSubmit={saveDetail} className="max-h-[90vh] w-full max-w-2xl space-y-5 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-ivory">{detail.name}</h3>
                <p className="text-xs text-ivory/45">{detail.email} · {detail.phone || "—"}</p>
              </div>
              <button type="button" onClick={() => setDetail(null)} aria-label="Fermer">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                  Anniversaire
                </label>
                <input type="date" value={detail.birthday || ""} onChange={(e) => setDetail((d) => ({ ...d, birthday: e.target.value }))} className="input-dark !h-11 [color-scheme:dark]" data-testid="crm-birthday-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                  Préférences
                </label>
                <input value={detail.preferences || ""} onChange={(e) => setDetail((d) => ({ ...d, preferences: e.target.value }))} placeholder="Ex : sans piment, table terrasse" className="input-dark !h-11" data-testid="crm-preferences-input" />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                Notes internes
              </label>
              <textarea rows={2} value={detail.notes || ""} onChange={(e) => setDetail((d) => ({ ...d, notes: e.target.value }))} className="input-dark !h-auto py-3" data-testid="crm-notes-input" />
            </div>

            <div className="space-y-4 border-t border-white/10 pt-5" data-testid="crm-history">
              <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">
                Historique — {detail.reservations?.length || 0} réservation(s), {detail.orders?.length || 0} commande(s), {detail.service_bookings?.length || 0} prestation(s)
              </h4>
              <div className="max-h-56 space-y-1.5 overflow-y-auto pr-2">
                {(detail.reservations || []).map((r) => (
                  <p key={r.id} className="rounded-md border border-white/5 px-3 py-2 text-xs text-ivory/60">
                    Réservation — {r.date} {r.time} · {r.guests} pers. · {r.status === "confirmee" ? "Confirmée" : r.status === "annulee" ? "Annulée" : "En attente"}
                  </p>
                ))}
                {(detail.orders || []).map((o) => (
                  <p key={o.id} className="rounded-md border border-white/5 px-3 py-2 text-xs text-ivory/60">
                    Commande {(o.source || "emporter") === "comptoir" ? "Comptoir" : "à emporter"} — {o.date} · {fmt(o.total)} · {o.status === "confirmee" ? "Payée" : "En attente"}
                  </p>
                ))}
                {(detail.service_bookings || []).map((b) => (
                  <p key={b.id} className="rounded-md border border-white/5 px-3 py-2 text-xs text-ivory/60">
                    Prestation — {b.date} {b.time} · {fmt(b.amount)} · {b.status === "confirmee" ? "Payée" : "En attente"}
                  </p>
                ))}
              </div>
            </div>

            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="crm-save-button">
              {saving ? "Enregistrement…" : "Enregistrer la fiche"}
            </button>
          </form>
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="text-sm text-ivory/40" data-testid="crm-empty">
          Aucun client pour le moment. Les fiches se créent automatiquement après le premier paiement.
        </p>
      ) : (
        <div className="space-y-2" data-testid="crm-list">
          {filtered.map((c) => (
            <button
              key={c.id}
              onClick={() => openDetail(c.id)}
              className="flex w-full flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5 text-left transition-colors hover:border-gold/40"
              data-testid={`crm-client-${c.id}`}
            >
              <div className="min-w-44">
                <p className="text-sm font-semibold text-ivory">{c.name}</p>
                <p className="text-xs text-ivory/45">{c.email}</p>
              </div>
              <span className="text-xs text-ivory/50">{c.visits} visite(s)</span>
              <span className="text-xs text-gold">{fmt(c.total_spent)}</span>
              <span className="flex items-center gap-1 text-xs text-copper">
                <Star className="h-3.5 w-3.5" strokeWidth={1.5} />
                {c.loyalty_points} pts
              </span>
              {c.birthday && (
                <span className="flex items-center gap-1 text-xs text-ivory/40">
                  <Cake className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {c.birthday.slice(5).split("-").reverse().join("/")}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
